import type { Page } from '../../app/feature';
import type { PanelUser } from '../../shared/types';
import { delegate, esc, inputValue, q, qa, setInputValue } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { money } from '../../shared/format';
import { parseMoney } from '../../shared/ui/money-input';
import { ApiError } from '../../shared/api/http';
import { hydrateFromApi } from '../../shared/store/sync';
import { toast } from '../../shared/ui/toast';
import { href, navigate } from '../../app/router';
import { eventsApi } from './events.api';
import { prizesApi } from '../prizes/prizes.api';
import { usersApi } from '../users/users.api';

interface PrizeDraft { emoji: string; name: string; value: number; }

const EMOJIS = ['🎁', '💰', '🎰', '👕', '🎧', '📱', '🏆', '🎟', '💎', '🚗'];

let prizes: PrizeDraft[] = [];
let hosts: PanelUser[] = [];
let submitting = false;

/**
 * Criar sorteio em três passos, tudo de uma vez: quando é a live, quais prêmios entram na roda e
 * quem conduz. Ao confirmar, cria o sorteio, os prêmios na ordem e atribui o anfitrião; depois
 * abre a página do sorteio já completa.
 */
export const eventNew: Page = {
  id: 'sorteo-nuevo',
  pattern: '/sorteos/nuevo',
  roles: ['ADMIN'],
  navParent: 'sorteos',

  template: () => `
<div class="crumbs"><a class="auth-link" href="${href('/sorteos')}" data-i18n="ev.back">← Sorteos</a></div>
<div class="view-head">
  <div>
    <h2 data-i18n="evn.title">Nuevo sorteo</h2>
    <p data-i18n="evn.sub">Define cuándo es la live, qué premios entran en la rueda y quién la conduce. Todo se puede ajustar después dentro de la página del sorteo.</p>
  </div>
</div>

<form id="evn-form" class="cols" novalidate autocomplete="off">
  <div class="span-8 stack">

    <!-- 1. Quando -->
    <section class="mis-step">
      <div class="mis-step-head"><span class="mis-num">1</span><div><h4 data-i18n="evn.s1">¿Cuándo es la live?</h4><p data-i18n="evn.s1d">El nombre aparece para los jugadores en el sitio y para el anfitrión en el estudio.</p></div></div>
      <div class="form-grid">
        <div class="field full"><label for="evn-name" data-i18n="ev.name">Nombre de la transmisión</label><input type="text" id="evn-name" placeholder="Live semanal · Viernes" required></div>
        <div class="field"><label data-i18n="ev.type">Tipo</label>
          <div class="seg" id="evn-tier">
            <label><input type="radio" name="evn-tier" value="SEMANAL" checked><span data-i18n="tier.weekly">Semanal</span></label>
            <label><input type="radio" name="evn-tier" value="MENSUAL"><span data-i18n="tier.monthly">Mensual</span></label>
          </div>
        </div>
        <div class="field when-field">
          <label for="evn-date" data-i18n="evn.when">Día y hora</label>
          <div class="when-row">
            <input type="date" id="evn-date" class="when-date" required>
            <input type="time" id="evn-time" class="when-time" value="21:00" step="300" required>
          </div>
          <span class="hint when-preview" id="evn-when-preview"></span>
        </div>
        <div class="field full"><label for="evn-link" data-i18n="ev.link">Link de la transmisión (opcional)</label><input type="url" id="evn-link" placeholder="https://…"></div>
      </div>
    </section>

    <!-- 2. Prêmios -->
    <section class="mis-step">
      <div class="mis-step-head"><span class="mis-num">2</span><div><h4 data-i18n="evn.s2">¿Qué premios entran en la rueda?</h4><p data-i18n="evn.s2d">Agrega uno por uno. El orden aquí es el orden en que la rueda los ofrece en la live; puedes reordenar con las flechas.</p></div></div>
      <div class="row" style="align-items:end">
        <div class="field" style="flex:0 0 96px"><label for="evn-p-emoji" data-i18n="f.icon">Ícono</label>
          <select id="evn-p-emoji">${EMOJIS.map(e => `<option value="${e}">${e}</option>`).join('')}</select></div>
        <div class="field" style="flex:2 1 200px"><label for="evn-p-name" data-i18n="f.prize">Premio</label><input type="text" id="evn-p-name" placeholder="Bono $50.000 CLP"></div>
        <div class="field" style="flex:1 1 140px"><label for="evn-p-value" data-i18n="f.valueOpt">Valor (CLP) · opcional</label><input type="text" inputmode="numeric" class="money" id="evn-p-value" placeholder="$50.000"></div>
        <div class="field" style="flex:0 0 auto"><button type="button" class="btn" id="evn-p-add">+ <span data-i18n="common.add">Agregar</span></button></div>
      </div>
      <div class="evn-prizes" id="evn-prizes"></div>
    </section>

    <!-- 3. Anfitrião -->
    <section class="mis-step">
      <div class="mis-step-head"><span class="mis-num">3</span><div><h4 data-i18n="evn.s3">¿Quién conduce la live?</h4><p data-i18n="evn.s3d">El influencer entra con su propio acceso y ve sólo el estudio de este sorteo. Puedes dejarlo para después.</p></div></div>
      <div class="row" style="align-items:end">
        <div class="field" style="flex:1 1 260px"><label for="evn-host" data-i18n="th.host">Anfitrión</label><select id="evn-host"></select></div>
        <div class="field" style="flex:0 0 auto"><a class="btn btn-sm" href="${href('/accesos')}" data-i18n="ev.newHost">Crear acceso</a></div>
      </div>
      <p class="hint" id="evn-host-hint"></p>
    </section>

    <div class="auth-error" id="evn-error" role="alert"></div>
  </div>

  <!-- Resumo -->
  <aside class="span-4">
    <div class="panel sticky evn-summary">
      <div class="panel-head"><h3 data-i18n="evn.summary">Resumen</h3></div>
      <div class="evn-sum-name" id="evn-sum-name">—</div>
      <div class="evn-sum-when muted" id="evn-sum-when">—</div>
      <div class="stats" style="margin-top:14px">
        <div style="--accent:var(--orange)"><b id="evn-sum-count">0</b><span data-i18n="host.prizes">Premios</span></div>
        <div style="--accent:var(--gold)"><b id="evn-sum-pozo">$0</b><span data-i18n="stage.pozo">Pozo</span></div>
      </div>
      <div class="evn-sum-host" id="evn-sum-host"></div>
      <ul class="evn-check" id="evn-check"></ul>
      <button type="submit" class="btn btn-primary" id="evn-submit" style="width:100%; margin-top:14px; padding:12px" data-i18n="evn.create">Crear sorteo</button>
      <p class="hint" style="margin-top:8px" data-i18n="evn.createHint">Se crea el sorteo, los premios en este orden y se asigna el anfitrión. Después abre la página del sorteo.</p>
    </div>
  </aside>
</form>`,

  mount() {
    const form = q<HTMLFormElement>('#evn-form');
    form.addEventListener('input', paintSummary);
    form.addEventListener('change', paintSummary);

    q('#evn-p-add').onclick = addPrize;
    q<HTMLInputElement>('#evn-p-value').addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); addPrize(); } });
    q<HTMLInputElement>('#evn-p-name').addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); addPrize(); } });

    const list = q('#evn-prizes');
    delegate(list, 'click', '[data-del]', el => { prizes.splice(Number(el.dataset.del), 1); paintPrizes(); paintSummary(); });
    delegate(list, 'click', '[data-move]', el => {
      const i = Number(el.dataset.idx), j = el.dataset.move === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= prizes.length) return;
      [prizes[i], prizes[j]] = [prizes[j]!, prizes[i]!];
      paintPrizes(); paintSummary();
    });

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (submitting) return;
      const error = q('#evn-error'); error.textContent = '';
      const name = inputValue('#evn-name').trim();
      const when = whenValue('#evn-date', '#evn-time');
      if (!name) { error.textContent = t('evn.errName'); q('#evn-name').focus(); return; }
      if (!when) { error.textContent = t('evn.errDate'); q('#evn-date').focus(); return; }
      if (!prizes.length && !confirm(t('evn.confirmNoPrizes'))) return;

      submitting = true;
      const submit = q<HTMLButtonElement>('#evn-submit'); submit.disabled = true;
      try {
        const created = await eventsApi.create({
          name, tier: q<HTMLInputElement>('input[name="evn-tier"]:checked').value as 'SEMANAL' | 'MENSUAL',
          scheduledAt: new Date(when).toISOString(),
          liveUrl: inputValue('#evn-link').trim() || undefined,
        });
        for (const p of prizes) await prizesApi.create(created.id, { name: p.name, value: p.value, emoji: p.emoji });
        const hostId = q<HTMLSelectElement>('#evn-host').value;
        if (hostId) await usersApi.update(hostId, { eventId: created.id });
        toast(t('evn.created'));
        reset();
        await hydrateFromApi();
        navigate('/sorteos/' + created.id);
      } catch (err) {
        error.textContent = err instanceof ApiError ? err.message : t('auth.errGeneric');
      } finally {
        submitting = false; submit.disabled = false;
      }
    });
  },

  render() {
    void loadHosts();
    paintPrizes();
    paintSummary();
  }
};

function addPrize(): void {
  const name = inputValue('#evn-p-name').trim();
  const value = parseMoney(inputValue('#evn-p-value'));
  if (!name) { q('#evn-p-name').focus(); return; }
  prizes.push({ emoji: q<HTMLSelectElement>('#evn-p-emoji').value, name, value });
  setInputValue('#evn-p-name', ''); setInputValue('#evn-p-value', '');
  q('#evn-p-name').focus();
  paintPrizes(); paintSummary();
}

function paintPrizes(): void {
  q('#evn-prizes').innerHTML = prizes.map((p, i) => `
    <div class="evn-prize">
      <span class="evn-prize-n">${String(i + 1).padStart(2, '0')}</span>
      <span class="evn-prize-emoji">${p.emoji}</span>
      <b>${esc(p.name)}</b>
      <span class="t-num">${p.value > 0 ? money(p.value) : `<span class="muted">${esc(t('ev.noValue'))}</span>`}</span>
      <span class="evn-prize-actions">
        <button type="button" class="btn btn-sm btn-ghost" data-move="up" data-idx="${i}" ${i === 0 ? 'disabled' : ''}>↑</button>
        <button type="button" class="btn btn-sm btn-ghost" data-move="down" data-idx="${i}" ${i === prizes.length - 1 ? 'disabled' : ''}>↓</button>
        <button type="button" class="btn btn-sm btn-ghost" data-del="${i}" title="${esc(t('common.del'))}">✕</button>
      </span>
    </div>`).join('') || `<div class="empty" style="padding:14px">${esc(t('evn.noPrizes'))}</div>`;
}

async function loadHosts(): Promise<void> {
  try { hosts = await usersApi.list('HOST'); } catch { hosts = []; }
  const sel = q<HTMLSelectElement>('#evn-host');
  const keep = sel.value;
  sel.innerHTML = `<option value="">${esc(t('evn.hostLater'))}</option>` +
    hosts.filter(h => h.active).map(h => `<option value="${h.id}">${esc(h.name)}${h.handle ? ` (${esc(h.handle)})` : ''}${h.eventId ? ` · ${esc(t('ev.busy'))}` : ''}</option>`).join('');
  if (keep) sel.value = keep;
  paintSummary();
}

function paintSummary(): void {
  const name = inputValue('#evn-name').trim();
  const when = whenValue('#evn-date', '#evn-time');
  const tier = qa<HTMLInputElement>('input[name="evn-tier"]').find(r => r.checked)?.value ?? 'SEMANAL';
  const hostId = q<HTMLSelectElement>('#evn-host').value;
  const host = hosts.find(h => h.id === hostId);
  const pozo = prizes.reduce((s, p) => s + p.value, 0);

  q('#evn-when-preview').textContent = when ? formatLocal(when) : '';
  q('#evn-sum-name').textContent = name || t('evn.noName');
  q('#evn-sum-when').textContent = `${tier === 'MENSUAL' ? t('tier.monthly') : t('tier.weekly')} · ${when ? formatLocal(when) : t('evn.noDate')}`;
  q('#evn-sum-count').textContent = String(prizes.length);
  q('#evn-sum-pozo').textContent = money(pozo);
  q('#evn-sum-host').innerHTML = host
    ? `🎙 <b>${esc(host.name)}</b>${host.eventId ? `<div class="hint">${esc(t('evn.hostBusy'))}</div>` : ''}`
    : `<span class="muted">🎙 ${esc(t('evn.hostLater'))}</span>`;
  q('#evn-host-hint').textContent = host?.eventId ? t('evn.hostBusy') : '';

  const checks: [boolean, string][] = [
    [!!name, t('evn.ckName')], [!!when, t('evn.ckDate')], [prizes.length > 0, t('evn.ckPrizes')], [!!host, t('evn.ckHost')],
  ];
  q('#evn-check').innerHTML = checks.map(([ok, label]) => `<li class="${ok ? 'ok' : ''}"><i>${ok ? '✓' : '○'}</i>${esc(label)}</li>`).join('');
}

/** Junta os campos de dia e hora num valor `YYYY-MM-DDTHH:mm`; vazio se faltar o dia. */
function whenValue(dateSel: string, timeSel: string): string {
  const d = inputValue(dateSel), h = inputValue(timeSel) || '21:00';
  return d ? `${d}T${h}` : '';
}

function formatLocal(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const wd = d.toLocaleDateString(undefined, { weekday: 'long' });
  const dd = String(d.getDate()).padStart(2, '0'), mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0'), mi = String(d.getMinutes()).padStart(2, '0');
  return `${wd}, ${dd}/${mm} · ${hh}:${mi}`;
}

function reset(): void {
  prizes = [];
  q<HTMLFormElement>('#evn-form').reset();
  paintPrizes();
}
